import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Panel, PanelHeader } from "@/components/ui/panel";
import { api } from "@/lib/api";
import { useOrg } from "@/lib/org-context";

type CustomerWebhook = {
  id: string;
  url: string;
  events: string[];
  createdAt: number;
};

/**
 * Minimal outbound webhooks UI (Phase 10).
 * Dispatches on stream execution.succeeded / execution.failed with HMAC signature.
 */
export function OutboundWebhooksPanel() {
  const { orgId, canManage } = useOrg();
  const [webhooks, setWebhooks] = useState<CustomerWebhook[]>([]);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);

  async function refresh() {
    const r = await api<{ webhooks: CustomerWebhook[] }>(
      `/api/accounts/${encodeURIComponent(orgId)}/webhooks`,
    );
    setWebhooks(
      (r.webhooks ?? []).map((w) => {
        const raw = (w as { events?: string[] | string }).events;
        const events = Array.isArray(raw)
          ? raw
          : typeof raw === "string"
            ? (JSON.parse(raw) as string[])
            : [];
        return { ...w, events };
      }),
    );
  }

  useEffect(() => {
    void refresh().catch(() => setWebhooks([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  async function addWebhook() {
    if (!canManage || !url.startsWith("https://")) {
      setError("Enter an HTTPS webhook URL.");
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    setCreatedSecret(null);
    try {
      const r = await api<{ id: string; secret: string }>(
        `/api/accounts/${encodeURIComponent(orgId)}/webhooks`,
        {
          method: "POST",
          body: JSON.stringify({
            url,
            events: ["execution.succeeded", "execution.failed"],
          }),
        },
      );
      setCreatedSecret(r.secret);
      setUrl("");
      setMessage(
        "Webhook created. Copy the signing secret now — it is shown only once.",
      );
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create webhook");
    } finally {
      setBusy(false);
    }
  }

  async function removeWebhook(id: string) {
    if (!canManage) return;
    setBusy(true);
    setError(null);
    try {
      await api(
        `/api/accounts/${encodeURIComponent(orgId)}/webhooks/${encodeURIComponent(id)}`,
        {
          method: "DELETE",
        },
      );
      setMessage("Webhook removed.");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not remove webhook");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel>
      <PanelHeader
        title="Outbound Webhooks"
        description="Receive signed HTTPS callbacks when a sync stream execution succeeds or fails"
      />
      <div className="space-y-4 p-5">
        <p className="text-sm text-rs-ink-muted">
          Payloads include event type, timestamp, and execution summary. Verify{" "}
          <code className="text-xs">X-Registrant-Sync-Signature</code>{" "}
          (HMAC-SHA256 hex of the body with your secret).
        </p>

        {webhooks.length === 0 ? (
          <p className="text-sm text-rs-ink-muted">
            No outbound webhooks configured.
          </p>
        ) : (
          <ul className="divide-y divide-rs-border rounded-rs-sm border border-rs-border">
            {webhooks.map((hook) => (
              <li
                key={hook.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-rs-ink">{hook.url}</p>
                  <p className="text-xs text-rs-ink-muted">
                    {(hook.events ?? []).join(", ")}
                  </p>
                </div>
                {canManage ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy}
                    onClick={() => void removeWebhook(hook.id)}
                  >
                    Remove
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {canManage ? (
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[16rem] flex-1 space-y-1.5">
              <Label htmlFor="outbound-webhook-url">Webhook URL</Label>
              <Input
                id="outbound-webhook-url"
                type="url"
                placeholder="https://example.com/hooks/attendeesync"
                value={url}
                disabled={busy}
                onChange={(e) => setUrl(e.target.value)}
              />
            </div>
            <Button
              disabled={busy || !url.trim()}
              onClick={() => void addWebhook()}
            >
              Add Webhook
            </Button>
          </div>
        ) : null}

        {createdSecret ? (
          <p className="rounded-rs-sm border border-rs-border bg-rs-muted/40 px-3 py-2 text-sm text-rs-ink">
            Signing secret (copy now):{" "}
            <code className="break-all text-xs">{createdSecret}</code>
          </p>
        ) : null}
        {message ? <p className="text-sm text-rs-success">{message}</p> : null}
        {error ? (
          <p role="alert" className="text-sm text-rs-danger">
            {error}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}
